import { useMemo } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import Button from "@atlaskit/button-next/new";
import EmptyState from "@atlaskit/empty-state";
import Heading from "@atlaskit/heading";
import SortAscendingIcon from "@atlaskit/icon/core/sort-ascending";
import SortDescendingIcon from "@atlaskit/icon/core/sort-descending";
import Link from "@atlaskit/link";
import { Box, Stack, Flex, xcss, Text } from "@atlaskit/primitives-next";
import Pressable from "@atlaskit/primitives-next/pressable";
import TableTree, {
    Cell,
    Header,
    Headers,
    Row,
    Rows
} from "@atlaskit/table-tree";
import Tooltip from "@atlaskit/tooltip";

import { DEFAULT_VALUES } from "@/libraries/web-framework";

import { useFireAnalyticsEvents } from "../../../../../analytics";
import {
    ActionKey,
    ActionSubjectIdKey,
    ActionSubjectKey,
    SourceKey
} from "../../../../../analytics/constants";
import { NO_ORG_ID } from "../../../../state";
import { Org } from "../../../../state/types";
import { StatusLozenge } from "../../status-lozenge";
import { ErrorIcon } from "../assets/error-icon";
import { SAC_URL } from "../constants";
import { getProductIcon } from "../utils/get-product-icon";
import { getStatusCategory } from "../utils/get-status-category";

import { messages } from "./messages";

type Row = {
    id: string;
    productType: string;
    sourceUrls: string;
    destinationUrl: string;
    phase: string;
    status: string;
};

type Header = {
    key: string;
    label: string;
    width: string;
};

type Headers = [Header, Header, Header, Header, Header];

const migrationStatusTableContainer = xcss({
    width: "100%",
    marginBlockEnd: "space.250"
});

const tableContainerStyles = xcss({
    overflowX: "auto"
});

const tableStyles = xcss({
    minWidth: "850px",
    position: "relative",
    borderRadius: "border.radius.100",
    borderColor: "color.border",
    borderWidth: "border.width",
    borderStyle: "solid"
});

const footerStyles = xcss({
    paddingInlineStart: "space.150",
    paddingBlock: "space.100"
});

const productSourceStyles = xcss({
    width: "30px",
    height: "30px",
    borderRadius: "border.radius.200",
    borderColor: "color.border",
    borderWidth: "border.width",
    borderStyle: "solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
});

const productDestinationStyles = xcss({
    width: "30px",
    height: "30px",
    borderRadius: "border.radius.200",
    backgroundColor: "color.background.accent.blue.bolder",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
});

const errorMessageContainerStyles = xcss({
    textAlign: "center",
    maxWidth: "300px"
});

const errorButtonStyles = xcss({
    marginBlockStart: "space.200"
});

const headerStyles = xcss({
    backgroundColor: "color.background.neutral.subtle",
    padding: "space.0"
});

const Product = ({
    url,
    productType,
    type
}: {
    url: string;
    productType: string;
    type: "source" | "destination";
}) => (
    <Flex gap="space.100" alignItems="center" columnGap="space.100">
        <Box
            xcss={
                type === "source"
                    ? productSourceStyles
                    : productDestinationStyles
            }
        >
            {getProductIcon({ productType, type })}
        </Box>

        <Stack space="space.0">
            <Text>{productType}</Text>
            <Text color="color.text.subtle" size="small">
                {url}
            </Text>
        </Stack>
    </Flex>
);

const Phase = ({ phase }: { phase: string }) => <Box as="span">{phase}</Box>;

const Status = ({ status }: { status: string }) => (
    <Box as="span">
        <StatusLozenge
            status={{
                text: status,
                category: getStatusCategory(status)
            }}
        />
    </Box>
);

const Ticket = ({ id }: { id: string }) => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const handleOnClick = () => {
        fireUIEvent({
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.LINK,
            actionSubjectId:
                ActionSubjectIdKey.FASTSHIFT_MIGRATION_PROGRESS_TABLE_OPEN_SUPPORT_TICKET_CLICK,
            attributes: {
                moveTicketId: id
            }
        });
    };

    return (
        <Flex alignItems="center" columnGap="space.050">
            <Link
                href={`${SAC_URL}/requests/${id}`}
                target="_blank"
                onClick={handleOnClick}
            >
                {id}
            </Link>
        </Flex>
    );
};

export const BaseStatusTable = ({
    rows,
    isLoading,
    hasError,
    numberOfTicketsToShow,
    selectedOrg,
    isOrgAdmin,
    sortKey,
    sortDirection,
    handleClickShowMore,
    handleSort,
    retryRequests
}: {
    rows: Row[];
    isLoading: boolean;
    hasError: boolean;
    numberOfTicketsToShow: number;
    selectedOrg: Org | null;
    isOrgAdmin: boolean;
    sortKey: string | null;
    sortDirection: "asc" | "desc";
    handleClickShowMore: () => void;
    handleSort: (key: string) => void;
    retryRequests: () => void;
}) => {
    const intl = useIntl();

    const headers: Headers = useMemo(
        () => [
            {
                key: "source",
                label: intl.formatMessage(
                    messages.fastshiftMigrationStatusTableSourceColumnTitle
                ),
                width: "25%"
            },
            {
                key: "destination",
                label: intl.formatMessage(
                    messages.fastshiftMigrationStatusTableDestinationColumnTitle
                ),
                width: "25%"
            },
            {
                key: "phase",
                label: intl.formatMessage(
                    messages.fastshiftMigrationStatusTablePhaseColumnTitle
                ),
                width: "15%"
            },
            {
                key: "status",
                label: intl.formatMessage(
                    messages.fastshiftMigrationStatusTableStatusColumnTitle
                ),
                width: "15%"
            },
            {
                key: "ticket",
                label: intl.formatMessage(
                    messages.fastshiftMigrationStatusTableTicketColumnTitle
                ),
                width: "20%"
            }
        ],
        [intl]
    );

    if (hasError && !isLoading) {
        return (
            <Box xcss={migrationStatusTableContainer}>
                <Flex alignItems="center" justifyContent="center">
                    <Box xcss={errorMessageContainerStyles}>
                        <ErrorIcon />

                        <Stack space="space.200">
                            <Heading size="small">
                                {intl.formatMessage(
                                    messages.fastshiftMigrationStatusTableErrorTitle
                                )}
                            </Heading>
                            <Text>
                                {intl.formatMessage(
                                    messages.fastshiftMigrationStatusTableErrorDescription
                                )}
                            </Text>
                        </Stack>

                        <Box xcss={errorButtonStyles}>
                            <Button
                                isDisabled={isLoading}
                                onClick={retryRequests}
                                appearance="primary"
                            >
                                {intl.formatMessage(
                                    messages.fastshiftMigrationStatusTableErrorRefreshButton
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Flex>
            </Box>
        );
    }

    return (
        <Box xcss={migrationStatusTableContainer}>
            <Stack space="space.200">
                <Heading size="small">
                    {selectedOrg?.id === NO_ORG_ID && isOrgAdmin
                        ? intl.formatMessage(
                              messages.fastshiftMigrationStatusTableTitleForUnassociatedTickets
                          )
                        : intl.formatMessage(
                              messages.fastshiftMigrationStatusTableTitle
                          )}
                </Heading>
                <Box xcss={tableContainerStyles}>
                    <Box xcss={tableStyles}>
                        <TableTree
                            columns={[Product, Product, Phase, Status, Ticket]}
                            columnWidths={headers.map(({ width }) => width)}
                        >
                            <Headers>
                                {headers.map(({ key, label }) => (
                                    <Header key={key}>
                                        <Pressable
                                            xcss={headerStyles}
                                            onClick={() => handleSort(key)}
                                        >
                                            <Flex
                                                alignItems="center"
                                                gap="space.050"
                                            >
                                                {label}
                                                {sortKey === key &&
                                                    (sortDirection === "asc" ? (
                                                        <SortAscendingIcon label="Sort ascending" />
                                                    ) : (
                                                        <SortDescendingIcon label="Sort descending" />
                                                    ))}
                                            </Flex>
                                        </Pressable>
                                    </Header>
                                ))}
                            </Headers>
                            <Rows
                                // undefined forces loading state
                                items={
                                    isLoading
                                        ? undefined
                                        : rows.slice(0, numberOfTicketsToShow)
                                }
                                render={({
                                    id,
                                    sourceUrls,
                                    productType,
                                    destinationUrl,
                                    phase,
                                    status
                                }) => (
                                    <Row itemId={id}>
                                        <Cell singleLine>
                                            <Tooltip
                                                content={
                                                    sourceUrls ||
                                                    intl.formatMessage(
                                                        messages.fastshiftMigrationStatusTableNoSourcePresent
                                                    )
                                                }
                                            >
                                                {(tooltipProps) => (
                                                    <Box {...tooltipProps}>
                                                        <Product
                                                            url={sourceUrls}
                                                            productType={
                                                                productType
                                                            }
                                                            type="source"
                                                        />
                                                    </Box>
                                                )}
                                            </Tooltip>
                                        </Cell>
                                        <Cell singleLine>
                                            <Product
                                                url={destinationUrl}
                                                productType={productType}
                                                type="destination"
                                            />
                                        </Cell>
                                        <Cell singleLine>
                                            <Phase phase={phase} />
                                        </Cell>
                                        <Cell singleLine>
                                            <Status status={status} />
                                        </Cell>
                                        <Cell singleLine>
                                            <Ticket id={id} />
                                        </Cell>
                                    </Row>
                                )}
                            />
                            {rows.length === 0 && !isLoading && (
                                <Rows
                                    items={[{ id: NO_ORG_ID }]}
                                    render={() => {
                                        return (
                                            <Row
                                                itemId={NO_ORG_ID}
                                                key={NO_ORG_ID}
                                            >
                                                <EmptyState
                                                    header={intl.formatMessage(
                                                        messages.fastshiftMigrationStatusTableEmptyTitle
                                                    )}
                                                    description={intl.formatMessage(
                                                        messages.fastshiftMigrationStatusTableEmptyDescription
                                                    )}
                                                />
                                            </Row>
                                        );
                                    }}
                                />
                            )}

                            <Rows
                                items={[{ id: "table-footer" }]}
                                render={({ id }) => (
                                    <Row itemId={id}>
                                        <Flex
                                            alignItems="center"
                                            gap="space.100"
                                            xcss={footerStyles}
                                        >
                                            <Text
                                                color="color.text.subtle"
                                                testId="status-table-page-count"
                                            >
                                                <FormattedMessage
                                                    {...messages.fastshiftMigrationStatusTablePageCount}
                                                    values={{
                                                        ...DEFAULT_VALUES,
                                                        numberOfTicketsToShow:
                                                            Math.min(
                                                                numberOfTicketsToShow,
                                                                rows.length
                                                            ),
                                                        totalTickets:
                                                            rows.length
                                                    }}
                                                />
                                            </Text>
                                            <Button
                                                onClick={handleClickShowMore}
                                                appearance="default"
                                                spacing="compact"
                                                isDisabled={
                                                    numberOfTicketsToShow >=
                                                    rows.length
                                                }
                                            >
                                                {intl.formatMessage(
                                                    messages.fastshiftMigrationStatusTableShowMoreFooterButton
                                                )}
                                            </Button>
                                        </Flex>
                                    </Row>
                                )}
                            />
                        </TableTree>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
};
